import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ===== إعدادات الـ API =====
const API_BASE_URL = process.env.REACT_APP_API_URL;

// ===== دوال جلب البيانات =====
const fetchProducts = async () => {
    const response = await fetch(`${API_BASE_URL}/products`);
    if (!response.ok) {
        throw new Error('فشل في جلب المنتجات');
    }
    return response.json();
};

// ===== Hook الرئيسي =====
export const useProducts = () => {
    const queryClient = useQueryClient();

    // ✅ جلب المنتجات مرة واحدة فقط
    const {
        data: products = [],
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['products'],
        queryFn: fetchProducts,
        staleTime: 10 * 60 * 1000, // ✅ البيانات صالحة لمدة 10 دقائق
        cacheTime: 30 * 60 * 1000, // ✅ تُحفظ في الذاكرة لمدة 30 دقيقة
        refetchOnWindowFocus: false, // ✅ لا تعيد الجلب عند العودة للتبويب
        refetchOnMount: false, // ✅ لا تعيد الجلب عند إعادة رندر المكون
        retry: 2, // ✅ حاول مرتين فقط في حالة الفشل
    });

    // ===== دوال التعديل (تُحدث البيانات تلقائياً) =====
    const addProductMutation = useMutation({
        mutationFn: async (formData) => {
            const response = await fetch(`${API_BASE_URL}/add`, {
                method: 'POST',
                body: formData,
            });
            if (!response.ok) throw new Error('فشل في إضافة المنتج');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['products']); // ✅ تحديث البيانات بعد الإضافة
        },
    });

    const updateProductMutation = useMutation({
        mutationFn: async ({ id, formData }) => {
            const response = await fetch(`${API_BASE_URL}/edit`, {
                method: 'PUT',
                body: formData,
            });
            if (!response.ok) throw new Error('فشل في تحديث المنتج');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['products']); // ✅ تحديث البيانات بعد التعديل
        },
    });

    const deleteProductMutation = useMutation({
        mutationFn: async (id) => {
            const response = await fetch(`${API_BASE_URL}/delete`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id }),
            });
            if (!response.ok) throw new Error('فشل في حذف المنتج');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['products']); // ✅ تحديث البيانات بعد الحذف
        },
    });

    return {
        products,
        isLoading,
        error,
        refetch, // ✅ دالة لإعادة الجلب (تُستخدم في الصفحة الرئيسية فقط)
        addProduct: addProductMutation.mutateAsync,
        updateProduct: updateProductMutation.mutateAsync,
        deleteProduct: deleteProductMutation.mutateAsync,
        isAdding: addProductMutation.isLoading,
        isUpdating: updateProductMutation.isLoading,
        isDeleting: deleteProductMutation.isLoading,
    };
};